// =====================================================================
//  ADMIN CONSOLE, FORMATTERS & SMALL HELPERS
// ---------------------------------------------------------------------
//  Pure functions only. Every page imports its formatting from here so
//  a date or a currency figure looks identical on every screen, the
//  old pages each hand-rolled their own and drifted apart.
//
//  All of them are defensive: the API returns plenty of nulls (salary,
//  join_date, end_date), and a dash is a better cell value than "NaN"
//  or "Invalid Date".
// =====================================================================

export const DASH = ',';

/* ------------------------------ numbers ------------------------------ */

export const isBlank = (v) => v === null || v === undefined || v === '';

/** 1234567 -> "12,34,567" (Indian grouping, matching the payroll domain). */
export const fmtNumber = (value, { decimals = 0 } = {}) => {
  if (isBlank(value) || Number.isNaN(Number(value))) return DASH;
  return Number(value).toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/** Currency with the rupee symbol. `compact` gives ₹12.3L / ₹1.2Cr. */
export const fmtMoney = (value, { compact = false, decimals = 0 } = {}) => {
  if (isBlank(value) || Number.isNaN(Number(value))) return DASH;
  const n = Number(value);
  if (compact) {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
    if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
    if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
    return `${sign}₹${abs.toFixed(0)}`;
  }
  return `₹${fmtNumber(n, { decimals })}`;
};

/** 12.5 -> "12.5%". Pass an already-computed percentage, not a ratio. */
export const fmtPercent = (value, { decimals = 1 } = {}) => {
  if (isBlank(value) || Number.isNaN(Number(value))) return DASH;
  const n = Number(value);
  const fixed = Number.isInteger(n) ? String(n) : n.toFixed(decimals);
  return `${fixed}%`;
};

/** Decimal hours -> "7h 30m". */
export const fmtHours = (hours) => {
  if (isBlank(hours) || Number.isNaN(Number(hours))) return DASH;
  const total = Math.max(0, Math.round(Number(hours) * 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/* ------------------------------- dates ------------------------------- */

/**
 * Parses the shapes this API actually returns: "YYYY-MM-DD" date strings
 * (pg is configured to hand back raw date strings) and ISO timestamps.
 * A bare date is pinned to local midnight so it never shifts a day.
 */
export const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const str = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(str) ? new Date(`${str}T00:00:00`) : new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "12 Mar 2026" */
export const fmtDate = (value) => {
  const d = parseDate(value);
  if (!d) return DASH;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "12 Mar", for axes and dense cells. */
export const fmtDateShort = (value) => {
  const d = parseDate(value);
  if (!d) return DASH;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

/** "9:05 am" */
export const fmtTime = (value) => {
  const d = parseDate(value);
  if (!d) return DASH;
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
};

/** "12 Mar 2026, 9:05 am" */
export const fmtDateTime = (value) => {
  const d = parseDate(value);
  if (!d) return DASH;
  return `${fmtDate(d)}, ${fmtTime(d)}`;
};

/** "just now" / "5m ago" / "3d ago", falling back to a date past a week. */
export const timeAgo = (value) => {
  const d = parseDate(value);
  if (!d) return DASH;
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 0) return fmtDate(d);
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return fmtDate(d);
};

/** ISO "YYYY-MM-DD" for a Date, in local time (not UTC). */
export const toISODate = (date = new Date()) => {
  const d = parseDate(date) || new Date();
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().split('T')[0];
};

export const addDays = (date, days) => {
  const d = parseDate(date) || new Date();
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
};

/** Inclusive whole-day span between two dates. */
export const daysBetween = (start, end) => {
  const s = parseDate(start);
  const e = parseDate(end) || s;
  if (!s || !e) return 0;
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
};

/** Tenure as "3y 2m" / "5m" / "12d". */
export const fmtTenure = (joinDate) => {
  const d = parseDate(joinDate);
  if (!d) return DASH;
  const now = new Date();
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) return DASH;
  if (months < 1) return `${Math.max(0, Math.floor((now - d) / 86400000))}d`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem}m`;
  return rem === 0 ? `${years}y` : `${years}y ${rem}m`;
};

/* ------------------------------- text -------------------------------- */

/** "Ada Lovelace" -> "AL"; falls back to the first letter of an email. */
export const initials = (name, email) => {
  const source = String(name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  const e = String(email || '').trim();
  return e ? e.slice(0, 2).toUpperCase() : '??';
};

export const titleCase = (value) =>
  String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const truncate = (value, max = 60) => {
  const s = String(value ?? '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/** Human label for a value that might be null/empty. */
export const orDash = (value) => (isBlank(value) ? DASH : String(value));

/* -------------------------------- CSV -------------------------------- */

const csvCell = (value) => {
  if (isBlank(value)) return '';
  const s = String(value);
  // Prefix a quote-escaped apostrophe on anything Excel would treat as a
  // formula. Without this, a cell like "=1+1" or "+91..." executes on open.
  const guarded = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

/**
 * Serializes rows to CSV.
 * @param {Array<object>} rows
 * @param {Array<{key:string,label?:string,csv?:Function}>|Array<string>} columns
 */
export const toCsv = (rows, columns) => {
  const cols = (columns || []).map((c) => (typeof c === 'string' ? { key: c } : c));
  const header = cols.map((c) => csvCell(c.label || titleCase(c.key))).join(',');
  const body = (rows || [])
    .map((row) => cols.map((c) => csvCell(c.csv ? c.csv(row) : row[c.key])).join(','))
    .join('\n');
  return `${header}\n${body}`;
};

/** Triggers a browser download of a CSV file. */
export const downloadCsv = (filename, rows, columns) => {
  // The BOM makes Excel read the file as UTF-8 (₹ and accents survive).
  const blob = new Blob([`\uFEFF${toCsv(rows, columns)}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/* ------------------------------ sorting ------------------------------ */

/**
 * Comparator that keeps blanks last and compares numbers numerically,
 * dates chronologically, and everything else case-insensitively.
 */
export const compareValues = (a, b, direction = 'asc') => {
  const dir = direction === 'desc' ? -1 : 1;
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1; // blanks last regardless of direction
  if (isBlank(b)) return -1;

  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return (na - nb) * dir;
  }

  const da = parseDate(a);
  const db = parseDate(b);
  if (da && db && /[-/:]/.test(String(a))) return (da - db) * dir;

  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }) * dir;
};

/** Case-insensitive "does any of these fields contain the term". */
export const matchesSearch = (row, term, keys) => {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return true;
  return (keys || Object.keys(row)).some((k) =>
    String(row?.[k] ?? '').toLowerCase().includes(q)
  );
};

/** Unique, sorted, non-empty values of a field, for building filter options. */
export const distinct = (rows, key) =>
  Array.from(
    new Set((rows || []).map((r) => r?.[key]).filter((v) => !isBlank(v)).map(String))
  ).sort((a, b) => a.localeCompare(b));
